import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { mapAccounts, toAccountRequest } from './accountMappers';
import type { ProviderAccount, SaveProviderAccountInput } from './accountTypes';
import { type ProviderConfigError, parsedErrorOrNull } from './errors';
import { useForgetRequest } from './forgetRequest';
import { providerAccountsQueryKey } from './queryKeys';
import { saveProviderAccountRequest } from './secretRequests';

export interface UseSaveProviderAccountResult {
    /** Resolves with the fresh account list; rejects with the raw error. */
    save: (providerName: string, input: SaveProviderAccountInput) => Promise<ProviderAccount[]>;
    isSaving: boolean;
    error: ProviderConfigError | null;
    /** Drops the request, typed secrets included, from the mutation cache. */
    reset: () => void;
}

export function useSaveProviderAccount(): UseSaveProviderAccountResult {
    const queryClient = useQueryClient();
    const { mutationKey, forget } = useForgetRequest('account');
    const mutation = useMutation({
        mutationKey,
        mutationFn: saveProviderAccountRequest,
        // The variables, and error.response.config.data on a failure, carry typed secrets.
        // Without this the mutation keeps them in the cache for 5 minutes after unmount;
        // while mounted they live until reset.
        gcTime: 0,
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: providerAccountsQueryKey(),
                refetchType: 'all',
            }),
    });
    const { mutateAsync } = mutation;

    const save = useCallback(
        async (providerName: string, input: SaveProviderAccountInput) => {
            const response = await mutateAsync({
                providerName,
                requestBody: toAccountRequest(input),
            });
            return mapAccounts(response.data);
        },
        [mutateAsync]
    );
    const error = useMemo(() => parsedErrorOrNull(mutation.error), [mutation.error]);
    const { reset: resetResult } = mutation;
    const reset = useCallback(() => {
        resetResult();
        forget();
    }, [resetResult, forget]);

    return { save, isSaving: mutation.isPending, error, reset };
}
